/**
 * DataLayer Service
 *
 * Low-level utility for interacting with Google Tag Manager's dataLayer.
 * Ensures window.dataLayer exists and provides typed push wrapper.
 */

/** GTM eventCallback signature */
export type EventCallback = () => void;

/** Base dataLayer event shape */
export interface DataLayerEvent {
  event: string;
  [key: string]: unknown;
}

/** Extended event with GTM callback support */
export interface DataLayerEventWithCallback extends DataLayerEvent {
  eventCallback?: EventCallback;
  eventTimeout?: number;
}

/**
 * Ensure window.dataLayer array exists.
 * Safe to call multiple times.
 */
function ensureDataLayer(): void {
  window.dataLayer = window.dataLayer || [];
}

/**
 * Push an event to the dataLayer.
 *
 * @param event - The event object to push
 */
export function pushToDataLayer(event: DataLayerEvent): void {
  ensureDataLayer();
  window.dataLayer.push(event);
}

/**
 * Push an event with GTM eventCallback + eventTimeout.
 * Used when navigation must wait for GTM to process the event.
 *
 * If GTM does not call eventCallback within `timeoutMs`, the callback
 * fires anyway (GTM's built-in eventTimeout behavior).
 *
 * Includes a safety setTimeout fallback in case GTM is not loaded at all.
 *
 * @param event - The event object (without eventCallback/eventTimeout)
 * @param callback - Function to call after GTM processes the event
 * @param timeoutMs - Maximum wait time before forcing callback (default 2000)
 */
export function pushWithCallback(
  event: DataLayerEvent,
  callback: EventCallback,
  timeoutMs: number = 2000
): void {
  ensureDataLayer();

  // Safety: if GTM is not loaded, eventCallback never fires.
  // Set a fallback setTimeout to ensure redirect always happens.
  let callbackFired = false;
  const safeCallback = () => {
    if (!callbackFired) {
      callbackFired = true;
      callback();
    }
  };

  window.dataLayer.push({
    ...event,
    eventCallback: safeCallback,
    eventTimeout: timeoutMs,
  });

  // Fallback: if GTM never processes the event, redirect anyway
  setTimeout(safeCallback, timeoutMs);
}

/**
 * TypeScript augmentation for window.dataLayer
 */
declare global {
  interface Window {
    dataLayer: Array<DataLayerEvent | DataLayerEventWithCallback>;
  }
}
